import LayoutContext from 'components/Layout/LayoutContext';
import Loading from 'components/Modal/__LEGACY__/AbrirVotacaoPautaModal/Loading';
import Breadcrumb, {
  IBreadCrumbItem,
} from 'components/__ASSESSOR__/BreadCrumb/BreadCrumb';
import AssembleiaDateTime from 'components/__SHARED__/AssembleiaDateTime';
import Page from 'components/__SHARED__/PrivateContainer';
import { ITopBarMenuItem } from 'components/__SHARED__/TopBar/TopBarMenuItem';
import { parseISO } from 'date-fns';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import { ParametroTipoResultado } from 'helpers/enums/Parametros';
import { TopBarMenuAcessoRapido } from 'helpers/enums/TopBarMenuAcessoRapido';
import AxiosErrorHandler from 'helpers/utils/AxiosErrorHandler';
import dotsOnLargeText from 'helpers/utils/dotsOnLargeText';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { getAcessoRapido } from 'helpers/utils/TopBarMenuUtils';
import {
  AssembleiaAssessorRoutePath,
  AssessorRelatoriosRoutePath,
  AssessorRoutePath,
} from 'library/Routes';
import translation from 'locales/translation';
import UnauthorizedScreen from 'pages/Errors/UnauthorizedScreen/UnauthorizedScreen';
import { Title } from 'plataforma-fundacao-componentes';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  assembleiasAssessorActionsAtualizarPresencas,
  assembleiasAssessorLoadEvento,
} from 'store/modules/AssembleiasAssessorActions';
import { IAssembleiasAssessorReducer } from 'store/modules/AssembleiasAssessorReducer';
import { IAuthReducer } from 'store/modules/AuthReducer';
import {
  setCurrentWSEvent,
  wsAssembleiasActionsClose,
  wsAssembleiasActionsOpen,
} from 'store/modules/WSAssembleiasActions';
import { IWSAssembleiasReducer } from 'store/modules/WSAssembleiasReducer';
import { IReducer } from 'store/Reducer';
import { history } from 'store/Store';
import Filtros from './Filtros';
import { QuorumInitialState, QuorumReducer } from './reducer';
import useStyle from './style';
import Tabela from './Tabela';
import Totais from './Totais';

interface PageParams {
  idAssembleia: string;
}

export default function Quorum() {
  const classes = useStyle();
  const location = useLocation();
  const { idAssembleia } = useParams<PageParams>();
  const layoutContext = useContext(LayoutContext);
  const { evento, loadingEvento } = useSelector<
    IReducer,
    IAssembleiasAssessorReducer
  >((state) => state.assembleiasAssessor);
  const { opened: wsOpened } = useSelector<IReducer, IWSAssembleiasReducer>(
    (state) => state.wsAssembleias
  );
  const { usuario } = useSelector<IReducer, IAuthReducer>(
    (state) => state.auth
  );
  const dispatch = useDispatch();
  const [state, action] = useReducer(QuorumReducer, QuorumInitialState);

  const breadcrumbItens = useMemo((): Array<IBreadCrumbItem> => {
    if (!evento) return [];
    return [
      {
        nome: translation.MainRoute.title,
        url: AssessorRoutePath,
      },
      {
        nome: dotsOnLargeText(evento.nome, 30),
        url: AssembleiaAssessorRoutePath.replace(':id', `${evento.id}`),
      },
      {
        nome: 'Quórum',
      },
    ];
  }, [evento]);

  const backToEvent = useCallback(() => {
    if (!evento) return;
    history.push(AssembleiaAssessorRoutePath.replace(':id', `${evento.id}`));
  }, [evento]);

  const needLoadEvento = useMemo(() => {
    if (!evento) return true;
    if (evento.id !== parseInt(idAssembleia)) return true;
    return false;
  }, [evento, idAssembleia]);

  const isAGN = useMemo(() => {
    if (!evento?.tipo) return false;
    return [AssembleiaTipo.Nucleo, AssembleiaTipo.Reuniao].includes(
      evento.tipo
    );
  }, [evento]);

  const quorumType = useMemo(() => {
    return isAGN ? 'Quórum atingido' : 'Representados';
  }, [isAGN]);

  const refresh = useCallback(() => {
    dispatch(
      assembleiasAssessorLoadEvento(
        parseInt(idAssembleia),
        undefined,
        (error) => AxiosErrorHandler(error)
      )
    );
  }, [dispatch, idAssembleia]);

  const atualizarTotais = useCallback(() => {
    action({ type: 'setLoadingTotais', value: true });
    dispatch(
      assembleiasAssessorActionsAtualizarPresencas(
        parseInt(idAssembleia),
        (value) => {
          action({ type: 'setTotais', value });
          action({ type: 'setReloadTable', value: true });
          action({ type: 'setLoadingTotais', value: false });
        },
        (fail) => {
          ErrorHandler(fail);
          action({ type: 'setLoadingTotais', value: false });
        }
      )
    );
  }, [dispatch, idAssembleia]);

  useEffect(() => {
    atualizarTotais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (needLoadEvento) {
      refresh();
    }
  }, [needLoadEvento, refresh]);

  useEffect(() => {
    if (!evento?.id || !usuario) return;
    if (!wsOpened && evento.id === parseInt(idAssembleia)) {
      dispatch(
        wsAssembleiasActionsOpen(
          usuario.tokenWs!,
          usuario.cpfCnpj!,
          evento.id,
          false,
          evento.status,
          () => setCurrentWSEvent(evento.id)
        )
      );
    }
    return () => {
      if (window.location.pathname !== location.pathname) {
        dispatch(wsAssembleiasActionsClose());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento, usuario, wsOpened, idAssembleia, dispatch]);

  const subMenu = useMemo<ITopBarMenuItem[]>(() => {
    if (!usuario) return [];
    const menus: ITopBarMenuItem[] = idAssembleia
      ? getAcessoRapido(
          translation,
          usuario!.perfil,
          TopBarMenuAcessoRapido.Quorum,
          idAssembleia,
          evento?.tipo
        )
      : [];

    if (evento?.id === parseInt(idAssembleia)) {
      menus.push({
        label: translation.SubMenus.relatorio,
        onClick: () =>
          window.open(
            AssessorRelatoriosRoutePath.replace(':id', idAssembleia.toString()),
            '_blank'
          ),
        disabled: false,
      });
    }

    return menus;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, state.agencia, usuario, idAssembleia]);

  useEffect(() => {
    layoutContext.setPageMenuItems(subMenu);
    return () => {
      layoutContext.setPageMenuItems();
    };
    // eslint-disable-next-line
  }, [state, usuario, evento, isAGN]);

  if (
    !usuario?.perfil.assessorRoute.assembleias.ver ||
    !usuario?.perfil.assessorRoute.assembleias.participantes.verTotal
  ) {
    return <UnauthorizedScreen />;
  }

  if (needLoadEvento) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }

  return (
    <>
      <Page
        headerClassName={classes.header}
        header={
          <React.Fragment>
            <div className={classes.headline}>
              <Title showBackButton onBackClick={backToEvent}>
                Quórum
              </Title>
            </div>
            <div className={classes.info}>
              <Breadcrumb itens={breadcrumbItens} />
            </div>
            <div className={classes.info}>
              <AssembleiaDateTime date={parseISO(evento!.dataEvento)} />
            </div>
          </React.Fragment>
        }
      >
        <Totais
          loading={state.loadingTotais}
          atualizar={(): any => {
            atualizarTotais();
            action({ type: 'setReloadTable', value: false });
          }}
          usuariosLoagdos={
            state.totais?.quantidadePessoasOnline !== undefined
              ? state.totais.quantidadePessoasOnline
              : 0
          }
          contasCapital={
            state.totais?.contasCapital !== undefined
              ? state.totais.contasCapital
              : evento!.contasCapital
          }
          presentes={
            state.totais?.presentes !== undefined
              ? state.totais.presentes
              : evento!.presentes
          }
          convidados={
            state.totais?.convidados !== undefined
              ? state.totais.convidados
              : evento!.convidados || 0
          }
          quorumAtingido={
            state.totais?.quorumAtingido !== undefined
              ? state.totais.quorumAtingido
              : evento!.quorumAtingido
          }
          aptos={
            state.totais?.presentesAptos !== undefined
              ? state.totais.presentesAptos
              : evento!.presentesAptos
          }
          isAGN={isAGN}
        />
        {evento?.id && (
          <React.Fragment>
            <Filtros
              idEvento={evento.id}
              tipoQuorum={quorumType}
              onChangeFilter={(value) => {
                action({ type: 'setFiltros', value });
              }}
              disableFlgTodos={
                isAGN && evento.tipoResultado === ParametroTipoResultado.evento
              }
            />
            <Tabela
              idEvento={evento.id}
              agencia={state.agencia}
              nucleo={state.nucleo}
              quorumAtingidoOrRepresentados={quorumType}
              tipoEvento={evento.tipo}
              flgTodos={state.flgTodos}
              needReload={state.needReloadTable}
              onReload={() => action({ type: 'setReloadTable', value: false })}
              reloadOnSend={() => {
                atualizarTotais();
              }}
              disableQuorum={false}
            />
          </React.Fragment>
        )}
        {(loadingEvento || state.loading) && <Loading />}
      </Page>
    </>
  );
}
